import { useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useMutationWrapper } from 'apis/config/use-api-wrapper';
import { RestaurantInquiryStatus, restaurantInquiriesEndpoint } from 'apis/restaurant-inquiries';
import type { CreateRestaurantInquiryItem } from 'apis/restaurant-inquiries';
import type { AxiosError } from 'axios';
import { useQueryClient } from 'react-query';
import { buildCreateInquiryPayload, pickNewestInquiry } from 'pages/sales-lookup/utils/build-inquiry-payload';

type TVariables = { restaurantId: number; items: CreateRestaurantInquiryItem[] };

// One submit = the whole basket into the restaurant's visit inquiry. Find the
// newest NEW inquiry for the restaurant (origin-agnostic — spec decision
// 2026-07-31) and append; create a CASH inquiry when none is open. Client-side
// find-or-create can race in theory; a single rep on a single phone makes that
// acceptable. Neither POST is idempotent — callers disable submit in flight.
export function useLogPriceToInquiry() {
  const toast = useCustomToast();
  const queryClient = useQueryClient();

  const mutationFn = async ({ restaurantId, items }: TVariables) => {
    try {
      const findResponse = await http.get(restaurantInquiriesEndpoint, {
        params: { restaurantId, status: RestaurantInquiryStatus.NEW, page: 1, limit: 5 },
      });
      const open = pickNewestInquiry<{ id: number }>(findResponse.data?.data ?? []);

      const response = open
        ? await http.post(`${restaurantInquiriesEndpoint}/${open.id}/items`, { items })
        : await http.post(restaurantInquiriesEndpoint, buildCreateInquiryPayload(restaurantId, items));

      return response.data?.data ?? response.data;
    } catch (error: any) {
      if (!error.response?.status || !error.response?.data?.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useMutationWrapper<any, AxiosError, TVariables>(mutationFn, {
    mutationKey: [restaurantInquiriesEndpoint, 'sales-log'],
    onSuccess: (_data, { items }) => {
      queryClient.invalidateQueries([restaurantInquiriesEndpoint]);
      toast({
        description: items.length === 1 ? '1 item added to the inquiry' : `${items.length} items added to the inquiry`,
        status: 'success',
      });
    },
  });
}
